import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common';

import { SingleSelectOption } from './single-select-types';

interface TriggerRendererProps {
  selectedOption: SingleSelectOption | null;
  placeholder: string;
  loading: boolean;
  isOpen: boolean;
  showAvatars: boolean;
  showDescriptionInSelector: boolean;
  avatarShape: 'circle' | 'square' | 'rounded';
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  hasClearButton?: boolean;
}

export function DefaultTriggerRenderer({
  selectedOption,
  placeholder,
  loading,
  isOpen,
  showAvatars,
  showDescriptionInSelector,
  avatarShape,
  avatarSize,
  hasClearButton = false,
}: TriggerRendererProps) {
  const { t } = useTranslation(['common']);

  return (
    <div className="flex min-h-[1.5rem] w-full items-center">
      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2',
          hasClearButton && selectedOption && 'pr-5'
        )}
      >
        {selectedOption ? (
          <>
            {showAvatars && (
              <EntityAvatar
                image={selectedOption.avatar}
                color={selectedOption.color}
                name={selectedOption.label}
                size={avatarSize ?? 'sm'}
                shape={avatarShape}
                // Keep the compact trigger avatar unless a caller explicitly requests a size.
                className={cn(!avatarSize && '!h-5 !w-5', 'flex-shrink-0')}
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-normal leading-tight">
                {selectedOption.label}
              </div>
              {showDescriptionInSelector && selectedOption.description && (
                <div className="truncate text-xs leading-tight text-muted-foreground">
                  {selectedOption.description}
                </div>
              )}
            </div>
          </>
        ) : (
          <span
            className={cn(
              'font-normal truncate text-sm',
              loading ? 'text-muted-foreground animate-pulse' : 'text-muted-foreground'
            )}
          >
            {loading ? t('loadingOptions') : placeholder}
          </span>
        )}
      </div>
      <ChevronDown
        className={cn(
          'h-4 w-4 flex-shrink-0 transition-transform opacity-50 ml-2',
          isOpen && 'rotate-180'
        )}
      />
    </div>
  );
}
